/**
 * IDE Setup Subcommand
 * Configure IDE settings (VSCode optimizations, etc.)
 * 
 * This is a wrapper that re-exports the existing vscode command
 * but as a subcommand under 'ide setup'
 */
import { Command } from 'commander';
import { checkbox, confirm, select } from '@inquirer/prompts';
import fs from 'node:fs/promises';
import path from 'node:path';
import { existsSync } from 'node:fs';
import { selectTheme, checkboxTheme } from '../../utils/prompt-theme.js';

// Performance Groups Definition
interface SettingsGroup {
    name: string;
    description: string;
    settings: Record<string, unknown>;
}

const PERFORMANCE_GROUPS: Record<string, SettingsGroup> = {
    telemetry: {
        name: 'Telemetry',
        description: 'Disable all telemetry and data collection',
        settings: {
            "telemetry.telemetryLevel": "off",
            "telemetry.feedback.enabled": false,
            "workbench.enableExperiments": false,
            "workbench.settings.enableNaturalLanguageSearch": false,
        }
    },
    languageServers: {
        name: 'Language Servers',
        description: 'Disable/reduce language server load (tsserver, eslint...)',
        settings: {
            // TypeScript
            "typescript.tsserver.experimental.enableProjectDiagnostics": false,
            "typescript.disableAutomaticTypeAcquisition": true,
            "typescript.tsserver.maxTsServerMemory": 2048,
            "typescript.surveys.enabled": false,
            // JavaScript
            "js/ts.implicitProjectConfig.strictNullChecks": false,
            // ESLint
            "eslint.enable": false,
            // Prettier
            "prettier.enable": false,
            // CSS/HTML
            "css.validate": false,
            "html.validate.scripts": false,
            "html.validate.styles": false,
            // JSON
            "json.schemaDownload.enable": false,
        }
    },
    git: {
        name: 'Git Integration',
        description: 'Disable built-in Git for better performance',
        settings: {
            "git.enabled": false,
            "git.autoRepositoryDetection": false,
            "git.decorations.enabled": false,
            "git.path": null,
            "scm.diffDecorations": "none",
        }
    },
    fileWatcher: {
        name: 'File Watcher',
        description: 'Exclude directories from file watcher',
        settings: {
            "files.watcherExclude": {
                "**/node_modules/**": true,
                "**/.git/**": true,
                "**/vendor/**": true,
                "**/dist/**": true,
                "**/build/**": true,
                "**/.next/**": true,
                "**/out/**": true,
                "**/.nuxt/**": true,
                "**/.cache/**": true,
                "**/coverage/**": true,
            },
            "files.exclude": {
                "**/.git": true,
                "**/.DS_Store": true,
                "**/node_modules": false, // Keep visible but don't watch
            }
        }
    },
    search: {
        name: 'Search',
        description: 'Exclude directories from search',
        settings: {
            "search.exclude": {
                "**/node_modules": true,
                "**/bower_components": true,
                "**/*.code-search": true,
                "**/dist": true,
                "**/build": true,
                "**/out": true,
                "**/.next": true,
                "**/.nuxt": true,
                "**/coverage": true,
                "**/.cache": true,
            },
            "search.followSymlinks": false,
        }
    },
    extensions: {
        name: 'Extensions',
        description: 'Disable auto-update extensions',
        settings: {
            "extensions.autoUpdate": false,
            "extensions.autoCheckUpdates": false,
            "update.mode": "manual",
            "extensions.ignoreRecommendations": true,
        }
    },
    editorRendering: {
        name: 'Editor Rendering',
        description: 'Optimize rendering (minimap, whitespace, highlights...)',
        settings: {
            "editor.minimap.enabled": false,
            "editor.renderWhitespace": "none",
            "editor.renderControlCharacters": false,
            "editor.renderLineHighlight": "none",
            "editor.cursorBlinking": "solid",
            "editor.smoothScrolling": false,
            "editor.largeFileOptimizations": true,
            "editor.maxTokenizationLineLength": 2000,
            "editor.quickSuggestions": {
                "other": true,
                "comments": false,
                "strings": false
            },
            "editor.suggest.showStatusBar": false,
            "editor.hover.delay": 500,
            "editor.matchBrackets": "never",
        }
    },
    uiElements: {
        name: 'UI Elements',
        description: 'Hide unnecessary UI elements',
        settings: {
            "workbench.activityBar.visible": false,
            "workbench.statusBar.visible": false,
            "breadcrumbs.enabled": false,
            "window.menuBarVisibility": "compact",
            "workbench.editor.showTabs": true, // Keep tabs for navigation
            "workbench.startupEditor": "none",
        }
    }
};

/**
 * Create setup subcommand for IDE group
 * Manages IDE settings (VSCode optimizations)
 */
export function createSetupSubcommand(): Command {
    const setupCommand = new Command('setup')
        .description('Configure IDE settings and optimizations');

    // Interactive mode (default)
    setupCommand.action(async () => {
        await interactiveMode();
    });

    // Enable subcommand
    setupCommand
        .command('enable')
        .description('Enable specific optimization groups')
        .argument('[groups...]', 'Group names to enable (telemetry, languageServers, git, ...)')
        .action(async (groups: string[]) => {
            if (groups.length === 0) {
                await selectGroupsToApply('enable');
            } else {
                await applyGroups(groups, 'enable');
            }
        });

    // Disable subcommand
    setupCommand
        .command('disable')
        .description('Disable specific optimization groups (restore to default)')
        .argument('[groups...]', 'Group names to disable')
        .action(async (groups: string[]) => {
            if (groups.length === 0) {
                await selectGroupsToApply('disable');
            } else {
                await applyGroups(groups, 'disable');
            }
        });

    // Max performance preset
    setupCommand
        .command('max-performance')
        .description('Enable all optimizations for maximum performance')
        .action(async () => {
            const allGroups = Object.keys(PERFORMANCE_GROUPS);
            await applyGroups(allGroups, 'enable');
        });

    // Reset subcommand
    setupCommand
        .command('reset')
        .description('Reset to default settings (remove all optimizations)')
        .argument('[groups...]', 'Group names to reset (empty = reset all)')
        .action(async (groups: string[]) => {
            await resetSettings(groups);
        });

    // List subcommand
    setupCommand
        .command('list')
        .description('List all available optimization groups')
        .action(() => {
            console.log('🔧  Available optimization groups:\n');
            Object.entries(PERFORMANCE_GROUPS).forEach(([key, group]) => {
                console.log(`  ${key.padEnd(20)} - ${group.name}`);
                console.log(`  ${' '.repeat(20)}   ${group.description}`);
                console.log();
            });
        });

    return setupCommand;
}

async function interactiveMode() {
    console.log('🔧  IDE Settings Manager\n');
    console.log('╭─────────────────────────────────────────────────────╮');
    console.log('│  📌 Usage:                                          │');
    console.log('│     • Use ↑↓ keys to navigate                       │');
    console.log('│     • Press SPACE to select/deselect group          │');
    console.log('│     • Press ENTER to confirm and apply              │');
    console.log('│     • Press Ctrl+C to cancel                        │');
    console.log('╰─────────────────────────────────────────────────────╯\n');

    const action = await select({
        message: 'What do you want to do?',
        choices: [
            { name: '✅ Enable optimization groups', value: 'enable' },
            { name: '❌ Disable optimization groups', value: 'disable' },
            { name: '🚀 Max Performance (enable all)', value: 'max' },
            { name: '🔄 Reset to defaults', value: 'reset' },
        ],
        theme: selectTheme,
    });

    if (action === 'max') {
        const allGroups = Object.keys(PERFORMANCE_GROUPS);
        await applyGroups(allGroups, 'enable');
    } else if (action === 'reset') {
        await resetSettings([]);
    } else {
        await selectGroupsToApply(action as 'enable' | 'disable');
    }
}

async function selectGroupsToApply(action: 'enable' | 'disable') {
    const choices = Object.entries(PERFORMANCE_GROUPS).map(([key, group]) => ({
        name: `${group.name} - ${group.description}`,
        value: key,
    }));

    try {
        const selectedGroups = await checkbox({
            message: `Select groups to ${action} (SPACE to select, ENTER to confirm):`,
            choices,
            theme: checkboxTheme,
        });

        if (selectedGroups.length === 0) {
            console.log('\n⚠️  No groups selected!');
            console.log('   💡 Tip: Press SPACE to select at least 1 group before pressing ENTER.');
            return;
        }

        await applyGroups(selectedGroups, action);
    } catch {
        console.log('\n❌ Operation cancelled.');
    }
}

async function applyGroups(groupKeys: string[], action: 'enable' | 'disable') {
    const vscodeDir = path.join(process.cwd(), '.vscode');
    const settingsPath = path.join(vscodeDir, 'settings.json');

    // Validate group keys
    const invalidGroups = groupKeys.filter(key => !PERFORMANCE_GROUPS[key]);
    if (invalidGroups.length > 0) {
        console.log(`\n❌ Invalid groups: ${invalidGroups.join(', ')}`);
        console.log('   💡 Run "jai1 ide setup list" to see available groups.');
        return;
    }

    // Ensure .vscode directory exists
    if (!existsSync(vscodeDir)) {
        await fs.mkdir(vscodeDir, { recursive: true });
        console.log('📁 Created .vscode/ directory');
    }

    // Read existing settings
    let currentSettings: Record<string, unknown> = {};
    if (existsSync(settingsPath)) {
        try {
            const content = await fs.readFile(settingsPath, 'utf-8');
            currentSettings = JSON.parse(content);
            console.log('📄 Read current settings from settings.json');
        } catch {
            console.warn('⚠️  Cannot read settings.json (may contain comments).');
            const confirmOverwrite = await confirm({
                message: 'Overwrite current settings.json file?',
                default: false
            });

            if (!confirmOverwrite) {
                console.log('❌ Operation cancelled.');
                return;
            }
            currentSettings = {};
        }
    }

    // Apply or remove settings
    const newSettings = { ...currentSettings };

    console.log(`\n📝 ${action === 'enable' ? 'Enabling' : 'Disabling'} groups:\n`);

    for (const key of groupKeys) {
        const group = PERFORMANCE_GROUPS[key];
        console.log(`   ${action === 'enable' ? '✓' : '✗'} ${group.name}`);

        if (action === 'enable') {
            // Merge settings
            for (const [settingKey, settingValue] of Object.entries(group.settings)) {
                if (typeof settingValue === 'object' && settingValue !== null && !Array.isArray(settingValue) &&
                    typeof newSettings[settingKey] === 'object' && newSettings[settingKey] !== null) {
                    newSettings[settingKey] = {
                        ...(newSettings[settingKey] as Record<string, unknown>),
                        ...settingValue
                    };
                } else {
                    newSettings[settingKey] = settingValue;
                }
            }
        } else {
            // Remove settings (disable)
            for (const settingKey of Object.keys(group.settings)) {
                delete newSettings[settingKey];
            }
        }
    }

    // Write back
    await fs.writeFile(settingsPath, JSON.stringify(newSettings, null, 2));
    console.log(`\n✅ Updated IDE settings at: ${settingsPath}`);
    console.log('💡 Tip: Restart your IDE to apply changes.');
}

async function resetSettings(groupKeys: string[]) {
    const vscodeDir = path.join(process.cwd(), '.vscode');
    const settingsPath = path.join(vscodeDir, 'settings.json');

    if (!existsSync(settingsPath)) {
        console.log('\n⚠️  No settings.json file found');
        return;
    }

    const confirmReset = await confirm({
        message: groupKeys.length === 0
            ? 'Reset ALL settings to default (delete entire file)?'
            : `Reset groups: ${groupKeys.join(', ')}?`,
        default: false
    });

    if (!confirmReset) {
        console.log('❌ Operation cancelled.');
        return;
    }

    if (groupKeys.length === 0) {
        // Reset all - delete file
        await fs.unlink(settingsPath);
        console.log('\n✅ Deleted settings.json file');
    } else {
        // Reset specific groups
        await applyGroups(groupKeys, 'disable');
    }

    console.log('💡 Tip: Restart your IDE to apply changes.');
}
